import { Alert, Button, CircularProgress, Stack } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { PasswordField, TextField } from '@/components/forms';
import { ROUTES } from '@/constants/routes';
import { AuthCard } from '@/features/identity/components/AuthCard';
import { AuthHeader } from '@/features/identity/components/AuthHeader';
import { RememberMeCheckbox } from '@/features/identity/components/RememberMeCheckbox';
import { SocialLoginButtons } from '@/features/identity/components/SocialLoginButtons';
import { useLoginForm } from '@/features/identity/hooks/useLoginForm';

/** The Login screen's form — client-side validation mirrors `loginSchema`; the backend remains
 * authoritative for the actual credential check. */
export function LoginForm() {
  const { form, onSubmit, serverError, needsVerification, email } = useLoginForm();
  const navigate = useNavigate();
  const { control, formState } = form;

  return (
    <AuthCard
      footer={
        <Stack spacing={0.5}>
          <span>
            Don&apos;t have an account? <RouterLink to={ROUTES.register}>Create one</RouterLink>
          </span>
          <span>
            Hiring? <RouterLink to={ROUTES.registerCompany}>Register your company</RouterLink>
          </span>
        </Stack>
      }
    >
      <AuthHeader title="Sign in" subtitle="Welcome back. Enter your details to continue." />
      <Stack component="form" onSubmit={onSubmit} spacing={2} noValidate>
        {serverError ? (
          <Alert
            severity="error"
            action={
              needsVerification ? (
                <Button
                  component={RouterLink}
                  to={ROUTES.verifyEmail}
                  state={{ email }}
                  size="small"
                  color="inherit"
                >
                  Verify email
                </Button>
              ) : undefined
            }
          >
            {serverError}
          </Alert>
        ) : null}
        <TextField name="email" control={control} label="Email" type="email" autoComplete="email" />
        <PasswordField
          name="password"
          control={control}
          label="Password"
          autoComplete="current-password"
        />
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <RememberMeCheckbox name="rememberMe" control={control} />
          <RouterLink to={ROUTES.forgotPassword}>Forgot password?</RouterLink>
        </Stack>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={formState.isSubmitting}
          startIcon={
            formState.isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          Sign in
        </Button>
      </Stack>
      {/* Drops errors ALREADY on screen — a failed submit, say — when the user gives up on the
          password path and reaches for Google, so nothing about the abandoned form is left showing
          behind the popup. The blur-triggered case is handled in the button itself, which declines
          focus; clearing cannot fix that one, because RHF's blur validation resolves after the click
          and would just put the error back. */}
      <SocialLoginButtons
        onStart={() => form.clearErrors()}
        onSuccess={() => navigate(ROUTES.root, { replace: true })}
      />
    </AuthCard>
  );
}
